import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import ResolvePopup from 'components/ResolvePopup';
import LikeIcon from 'icons/Like.icon';
import { Button, Loader } from 'ncoded-component-library';
import DislikeIcon from 'icons/Dislike.icon';
import CircledQuestionmarkIcon from 'icons/CircledQuestionmark.icon';
import useCacheFetch from 'hooks/useCacheFetch';
import { User } from 'models/User';
import { AxiosResponse } from 'axios';
import { getUserFullName } from 'utils';
import { ReactionUsers } from 'models/Reactions';
import { IconProp } from 'models/Icon';

import './ReactionList.styles.scss';

type ReactionListProps = {
  className?: string;
  thumbsUp: number;
  thumbsDown: number;
  questionMark: number;
  showPopup?: boolean;
  getReactionUsers?: () => Promise<AxiosResponse<ReactionUsers>>;
};

const ReactionList: React.FC<ReactionListProps> = (props) => {
  const {
    className,
    thumbsUp,
    thumbsDown,
    questionMark,
    showPopup = true,
    getReactionUsers,
  } = props;

  const classes = classNames('reaction-list', className);

  const {
    data,
    isLoading,
    fetch: fetchUsers,
  } = useCacheFetch({
    resetCache: [thumbsUp, thumbsDown, questionMark],
    fetchData: getReactionUsers,
  });

  const {
    thumbsUp: thumbsUpUsers,
    thumbsDown: thumbsDownUsers,
    questionMark: questionMarkUsers,
  } = data || {};

  const renderUsers = useCallback(
    (users: Partial<User>[], className: string, Icon: IconProp) => {
      if (!users?.length) return;

      return (
        <ul>
          {users.map(({ _id, firstName, lastName }) => {
            const fullName = getUserFullName(firstName, lastName);

            return (
              <li key={_id || fullName} className={className}>
                {fullName} <Icon />
              </li>
            );
          })}
        </ul>
      );
    },
    [],
  );

  const popupContent = useMemo(
    () => (
      <div className="reaction-list__popup">
        {isLoading && <Loader size="small" />}

        {renderUsers(thumbsUpUsers, 'like', LikeIcon)}
        {renderUsers(thumbsDownUsers, 'dislike', DislikeIcon)}
        {renderUsers(questionMarkUsers, 'question', CircledQuestionmarkIcon)}
      </div>
    ),
    [isLoading, questionMarkUsers, thumbsDownUsers, thumbsUpUsers, renderUsers],
  );

  const popupBase = useMemo(
    () => (
      <>
        {!!thumbsUp && (
          <Button
            icon={LikeIcon}
            className="like"
            variant="icon"
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        )}
        {!!thumbsDown && (
          <Button
            icon={DislikeIcon}
            className="dislike"
            variant="icon"
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        )}
        {!!questionMark && (
          <Button
            icon={CircledQuestionmarkIcon}
            className="question-mark"
            variant="icon"
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        )}
      </>
    ),
    [questionMark, thumbsDown, thumbsUp],
  );

  if (!showPopup)
    return (
      <div className={classNames(classes, 'reaction-list__content')}>
        {popupBase}
      </div>
    );

  return (
    <ResolvePopup
      className={classes}
      onHover
      popupContent={popupContent}
      customOnHover={fetchUsers}
      contentClassName="reaction-list__content"
    >
      {popupBase}
    </ResolvePopup>
  );
};

export default ReactionList;
